import { useState, useEffect, useRef } from "react";
import styles from "../ResourcesLayout.module.css";

/**
 * ResourcesTOC — Right-side "On this page" navigation.
 *
 * Props:
 *   headings: { id: string, text: string, level: number }[]
 *
 * Tracks scroll position and highlights the currently visible heading.
 * Hidden on screens ≤ 1280px via CSS.
 */
const ResourcesTOC = ({ headings }) => {
  const [activeId, setActiveId] = useState("");
  const observerRef = useRef(null);

  useEffect(() => {
    if (!headings || headings.length === 0) return;

    // Disconnect previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const callback = (entries) => {
      // Find the first entry that is intersecting
      const visibleEntries = entries.filter((entry) => entry.isIntersecting);
      if (visibleEntries.length > 0) {
        setActiveId(visibleEntries[0].target.id);
      }
    };

    observerRef.current = new IntersectionObserver(callback, {
      rootMargin: "-80px 0px -60% 0px",
      threshold: 0,
    });

    // Observe all heading elements
    for (const heading of headings) {
      const el = document.getElementById(heading.id);
      if (el) observerRef.current.observe(el);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [headings]);

  if (!headings || headings.length === 0) return null;

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <aside className={styles["docs-toc"]} role="navigation" aria-label="On this page">
      <div className={styles["toc-label"]}>On this page</div>
      <ul className={styles["toc-list"]}>
        {headings.map((heading) => (
          <li key={heading.id}>
            <a
              href={`#${heading.id}`}
              className={`${styles["toc-link"]} ${heading.level === 3 ? styles["toc-link-nested"] : ""} ${activeId === heading.id ? styles["toc-link-active"] : ""}`}
              onClick={(e) => handleClick(e, heading.id)}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
};

export default ResourcesTOC;
